<template>
  <div class="company-profile-tab">
    <div v-if="loading" class="profile-loading">
      <LoadingBars size="sm" />
    </div>

    <div v-else-if="error" class="profile-error">
      <Icon icon="mdi:alert-circle-outline" class="profile-error__icon" />
      <p class="profile-error__message">{{ error }}</p>
      <Button variant="primary" size="sm" @click="$emit('retry')">Try Again</Button>
    </div>

    <div v-else-if="!profile" class="profile-empty">
      <Icon icon="mdi:office-building-outline" class="profile-empty__icon" />
      <p>No company profile data available</p>
    </div>

    <div v-else class="profile-card">
      <div class="profile-header">
        <a
          v-if="profile.logoUrl && !logoFailed"
          :href="profile.logoUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="profile-avatar profile-avatar--clickable"
        >
          <img
            :src="profile.logoUrl"
            :alt="profile.companyName || 'Company logo'"
            @error="onLogoError"
          />
          <div class="profile-avatar__overlay">
            <Icon icon="mdi:open-in-new" class="profile-avatar__icon" />
          </div>
        </a>
        <div v-else class="profile-avatar">
          <div class="profile-avatar__placeholder">
            <Icon icon="mdi:office-building" width="28" height="28" />
          </div>
        </div>

        <div class="profile-meta">
          <h5>{{ profile.companyName || "" }}</h5>
          <div v-if="industries.length > 0" class="profile-industries">
            <span v-for="(industry, index) in industries" :key="index" class="industry-chip">
              {{ industry }}
            </span>
          </div>
          <p v-if="profile.headquarters" class="profile-location">{{ profile.headquarters }}</p>
        </div>
      </div>

      <div v-if="profile.description" class="profile-section">
        <h6>ABOUT</h6>
        <div class="profile-description">{{ profile.description }}</div>
      </div>

      <div v-if="overview.length > 0" class="profile-section">
        <h6>OVERVIEW</h6>
        <ul>
          <li v-for="(item, index) in overview" :key="index">{{ item }}</li>
        </ul>
      </div>

      <div v-if="profile.website" class="profile-section">
        <h6>WEBSITE</h6>
        <a
          :href="profile.website"
          target="_blank"
          rel="noopener noreferrer"
          class="profile-website-link"
        >
          {{ normalizeWebsite(profile.website) }}
          <Icon icon="mdi:open-in-new" />
        </a>
      </div>

      <div v-if="profile.employeeCount" class="profile-section">
        <h6>COMPANY SIZE</h6>
        <p class="profile-info-value">{{ formatEmployeeCount(profile.employeeCount) }}</p>
      </div>

      <div v-if="profile.foundedYear" class="profile-section">
        <h6>FOUNDED</h6>
        <p class="profile-info-value">{{ profile.foundedYear }}</p>
      </div>

      <div v-if="profile.companyType" class="profile-section">
        <h6>COMPANY TYPE</h6>
        <p class="profile-info-value">{{ profile.companyType }}</p>
      </div>

      <div
        v-if="profile.followersCount !== null && profile.followersCount !== undefined"
        class="profile-section"
      >
        <h6>FOLLOWERS</h6>
        <p class="profile-info-value">{{ formatNumber(profile.followersCount) }}</p>
      </div>

      <div v-if="profile.specialties && profile.specialties.length > 0" class="profile-section">
        <h6>SPECIALTIES</h6>
        <div class="specialties-container">
          <span
            v-for="(specialty, index) in profile.specialties"
            :key="index"
            class="specialty-tag"
          >
            {{ specialty }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { Icon } from "@iconify/vue";
import Button from "@/components/ui/Button.vue";
import LoadingBars from "@/components/ui/LoadingBars.vue";

interface ProfileData {
  companyName?: string;
  description?: string;
  industry?: string;
  website?: string;
  companyType?: string;
  employeeCount?: number | null;
  headquarters?: string;
  specialties?: string[];
  foundedYear?: number | null;
  logoUrl?: string;
  coverImageUrl?: string;
  followersCount?: number | null;
}

interface Props {
  profile?: ProfileData | null;
  loading?: boolean;
  error?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  profile: null,
  loading: false,
  error: null,
});

defineEmits<{
  (e: "retry"): void;
}>();

const logoFailed = ref(false);
const onLogoError = () => {
  logoFailed.value = true;
};

function summaryToBullets(summary?: string): string[] {
  if (!summary) return [];
  const trimmed = summary.trim();
  if (!trimmed) return [];
  if (trimmed.includes("\n")) {
    return trimmed
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 8);
  }
  const sentences = trimmed
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (sentences.length <= 1) return [trimmed];
  return sentences.slice(0, 8);
}

const overview = computed(() => summaryToBullets(props.profile?.description));

// Parse industry field - handle both string and array formats
const industries = computed(() => {
  const industry = props.profile?.industry;
  if (!industry) return [];

  if (Array.isArray(industry)) {
    return industry.filter(Boolean).map((item) => String(item).trim());
  }

  const industryStr = String(industry).trim();
  if (industryStr.startsWith("[") && industryStr.endsWith("]")) {
    try {
      const parsed = JSON.parse(industryStr);
      if (Array.isArray(parsed)) {
        return parsed.filter(Boolean).map((item) => String(item).trim());
      }
    } catch {
      const match = industryStr.match(/\["([^"]+)"\]/);
      if (match?.[1]) {
        return [match[1]];
      }
    }
  }

  return [industryStr];
});

const normalizeWebsite = (url?: string): string => {
  if (!url) return "";
  try {
    const urlObj = new URL(url.startsWith("http") ? url : `https://${url}`);
    return urlObj.hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

const formatEmployeeCount = (count?: number | null): string => {
  if (!count) return "";
  if (count >= 10000) return `${(count / 1000).toFixed(0)}k+ employees`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k employees`;
  return `${count} employees`;
};

const formatNumber = (num?: number | null): string => {
  if (!num) return "0";
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
  return num.toString();
};
</script>

<style scoped>
.company-profile-tab {
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
}

.profile-card {
  background: #fff;
  border: 1px solid #ececfb;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 6px 24px rgba(46, 61, 255, 0.05);
  flex: 1;
  min-height: 0;
}

.profile-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.profile-avatar {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  overflow: hidden;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.profile-avatar--clickable {
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
}

.profile-avatar--clickable:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border-color: #3e3efe;
}

.profile-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.profile-avatar__overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  border-radius: 8px;
}

.profile-avatar--clickable:hover .profile-avatar__overlay {
  opacity: 1;
}

.profile-avatar__icon {
  color: #fff;
  font-size: 18px;
}

.profile-avatar__placeholder {
  color: #9ca3af;
}

.profile-meta h5 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a2e;
}

.profile-industries {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 4px 0;
}

.industry-chip {
  background: #f3f4ff;
  color: #3e3efe;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.4;
}

.profile-location {
  font-size: 0.78rem;
  color: #8c8fa7;
  margin-bottom: 0;
}

.profile-section {
  margin-top: 16px;
}

.profile-section:first-of-type {
  margin-top: 16px;
}

.profile-section h6 {
  margin-bottom: 8px;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9ca0c8;
  font-weight: 600;
}

.profile-description {
  font-size: 0.875rem;
  color: #4c4f6a;
  line-height: 1.6;
  white-space: pre-wrap;
}

.profile-section ul {
  padding-left: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.78rem;
  color: #4c4f6a;
  list-style-type: none;
}

.profile-website-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.875rem;
  color: #3e3efe;
  text-decoration: none;
  font-weight: 500;
}

.profile-website-link:hover {
  text-decoration: underline;
}

.profile-info-value {
  font-size: 0.875rem;
  color: #4c4f6a;
  margin: 0;
  font-weight: 500;
}

.specialties-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.specialty-tag {
  background: #f3f4ff;
  color: #3e3efe;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
}

.profile-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  color: #6b7280;
}

.profile-error,
.profile-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
}

.profile-error__icon {
  font-size: 1.5rem;
  color: #ef4444;
  margin-bottom: 0.75rem;
}

.profile-error__message {
  color: #6b7280;
  margin: 0 0 1rem 0;
  max-width: 300px;
  line-height: 1.4;
  font-size: 0.875rem;
}

.profile-empty__icon {
  font-size: 2rem;
  margin-bottom: 0.75rem;
  opacity: 0.5;
  color: #9ca3af;
}

.profile-empty p {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}
</style>
